import { isSessionIdReusable, PENDING_CHAT_SESSION_ID } from './sessionId';
import getEmailAddressFromSipUri from '../../presenceManager/getEmailAddressFromSipUri';
import getMySipUri from '../../presenceManager/getMySipUri';
import isSignedIn from '../../presenceManager/isSignedIn';
import { addPresenceSubscription } from '../presenceManager/presenceSubscription';
import acceptChatSession from '../services/acceptChatSession';
import getPersona from '../../services/getPersona';
import { getStore } from '../store/ucmaStore';
import { ChatMessageType } from '../store/schema/ChatMessageViewState';
import ChatState from '../store/schema/ChatState';
import ChatViewState from '../store/schema/ChatViewState';
import StartChatSource from '../../store/schema/StartChatSource';
import { logUsage } from 'owa-analytics';
import InstantMessagePayload from 'owa-service/lib/contract/InstantMessagePayload';
import {
    addChat,
    addChatMessage,
    updateParticipantDisplayName,
    setChatState,
    updateSessionId,
} from '../actions/ucmaActions';
import {
    lazyAddNotification,
    ensureNotificationHandlerRegistered,
    NotificationActionType,
    NotificationViewState,
    IM_NOTIFICATION_TYPE,
    SkypeNotificationViewState,
} from 'owa-header-toast-notification';

export default function startChat(sipUris: string[], subject: string, source: StartChatSource) {
    const [chat, isReused] = startChatInternal(sipUris, subject);
    if (chat) {
        onActivateNewChat(chat, source, isReused);
        updatePersonaDisplayNames(sipUris);
    }
}

export function startChatWithName(
    namedSipUris: { sipUri: string; name: string }[],
    subject: string,
    source: StartChatSource
) {
    const sipUris = namedSipUris.map(item => item.sipUri);
    const [chat, isReused] = startChatInternal(sipUris, subject);
    if (chat) {
        onActivateNewChat(chat, source, isReused);
        namedSipUris.forEach(item => {
            updateParticipantDisplayName(item.sipUri, item.name);
        });
    }
}

function startChatInternal(sipUris: string[], subject: string): [ChatViewState, boolean] {
    let chat: ChatViewState;
    let isReused: boolean;
    if (isSignedIn()) {
        const mySipUri = getMySipUri();

        // Remove myself from the participant list
        sipUris = (sipUris || []).filter(uri => uri && uri.trim().toLowerCase() != mySipUri);

        if (sipUris.length > 0) {
            // Try to reuse existing chat if exist, otherwise create a new one
            chat = sipUris.length == 1 && findChatViewState(sipUris[0], subject);
            if (chat) {
                setChatState(chat, ChatState.Normal);
                isReused = true;
            } else {
                chat = createChatViewState(
                    PENDING_CHAT_SESSION_ID,
                    sipUris,
                    ChatState.Normal,
                    subject
                );
                addChat(chat);
            }
        }
    }
    return [chat, isReused];
}

export function initChatAndToastFromPayload(notification: InstantMessagePayload) {
    const { SipUri, DisplayName, MessageSubject, ChatSessionId, ToastMessage } = notification;

    updateParticipantDisplayName(SipUri, DisplayName);

    let chatViewState = findChatViewState(SipUri, MessageSubject);

    if (chatViewState) {
        updateSessionId(chatViewState, ChatSessionId);

        if (chatViewState.state == ChatState.Closed) {
            setChatState(chatViewState, ChatState.PendingAccept);
        }
    } else {
        chatViewState = createChatViewState(
            ChatSessionId,
            [SipUri],
            ChatState.PendingAccept,
            MessageSubject
        );

        addChat(chatViewState);
    }

    ensureNotificationHandlerRegistered(
        NotificationActionType.SkypeForBusinessNotification,
        onClickNotification(!!chatViewState)
    );

    // Only add a chat message if the subject + the toast message differ.
    // ToastMessage is what is to be rendered in the toast, but we hijack it for the 1:1 case to preserve the first message.
    // In group chats, they're usually the same since the subject is to be displayed.
    if (ToastMessage != MessageSubject) {
        addChatMessage(
            chatViewState,
            ChatMessageType.InstantMessageFromToast,
            SipUri,
            ToastMessage
        );
    }

    lazyAddNotification.importAndExecute({
        actionType: NotificationActionType.SkypeForBusinessNotification,
        title: DisplayName,
        description: ToastMessage,
        actionContext: chatViewState,
        type: IM_NOTIFICATION_TYPE,
        smtpAddress: getEmailAddressFromSipUri(SipUri),
        displayName: DisplayName,
    } as SkypeNotificationViewState);
}

const onClickNotification = (isReused: boolean) => (notification: NotificationViewState) => {
    if (notification.ignore || notification.timedOut) {
        return;
    }

    const chat = notification.actionContext as ChatViewState;
    acceptChatSession(chat.sessionId);
    setChatState(chat, ChatState.Normal);
    onActivateNewChat(chat, StartChatSource.NotificationToast, isReused);
};

let nextChatId = 0;

function createChatViewState(
    sessionId: number,
    participants: string[],
    state: ChatState,
    subject?: string
): ChatViewState {
    return {
        chatId: nextChatId++,
        sessionId,
        participants,
        messages: [],
        pendingMessage: '',
        lastMessageSequence: 0,
        lastNotificationSequence: 0,
        lastReadMessageSequence: 0,
        userActivity: {
            type: 'None',
            linkedUserSip: undefined,
        },
        subject: subject ? subject : '',
        state,
    };
}

function onActivateNewChat(chat: ChatViewState, source: StartChatSource, isReused: boolean) {
    addPresenceSubscription(chat.participants);
    const customData = [
        source, // The source of the chat initialization
        isReused, // If we were able to re-use a chat.
        chat.participants.length < 23 ? chat.participants.length : 23, // Total number of users in the chat.
    ];
    logUsage('UcmaSkypeStartChat', customData);
}

function findChatViewState(sipUri: string, subject: string): ChatViewState {
    subject = subject || '';
    const chats = getStore().chats;
    const chat = chats.filter(
        chat =>
            chat.participants.length == 1 &&
            chat.participants[0].toLowerCase() == sipUri &&
            isSessionIdReusable(chat.sessionId) &&
            chat.subject == subject
    )[0];

    return chat;
}

async function updatePersonaDisplayNames(sipUris: string[]) {
    const cache = getStore().participantCache;

    for (let i = 0; i < sipUris.length; i++) {
        const uri = sipUris[i];
        if (!cache.has(uri)) {
            const smtpAddress = getEmailAddressFromSipUri(uri);
            const persona = await getPersona(smtpAddress);
            if (persona) {
                updateParticipantDisplayName(uri, persona.DisplayName);
            }
        }
    }
}
